/**
 * FAQ audits list page.
 * 
 * @module pages/FAQAuditsPage
 * @see CSDEV-144 - Content/FAQ/LLMs pages: add business filter UI
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { DataTable, Pagination, Card } from '../components/ui';
import { useFAQAudits, usePagination, useAuditLog, useAllBusinesses, useExclusionRules } from '../hooks';
import { AuditActionTypes } from '../lib/audit';
import { isExcludedUser } from '../config/excluded-users';
import { formatDate, formatPercentage } from '../lib/utils';
import type { FAQAuditWithBusiness, DataTableColumn } from '../types';

/**
 * FAQ detail viewer component.
 */
function FAQDetailViewer({ audit }: { audit: FAQAuditWithBusiness }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <div className="flex-1">
          <p className="font-medium text-gray-900">
            {audit.businesses?.business_name || 'Unknown Business'}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            FAQ Score: {formatPercentage(audit.faq_score)} • Schema Score:{' '}
            {formatPercentage(audit.schema_score)}
          </p>
        </div>
        {isExpanded ? (
          <ChevronDown className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronRight className="h-5 w-5 text-gray-400" />
        )}
      </button>
      {isExpanded && audit.recommended_faq && (
        <div className="p-4 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Recommended FAQs</h4>
          <pre className="text-sm bg-gray-50 p-3 rounded-lg overflow-x-auto">
            {JSON.stringify(audit.recommended_faq, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

/**
 * FAQ audits list page component.
 * 
 * @returns FAQ audits page element
 */
export function FAQAuditsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { page, pageSize, goToPage, changePageSize, pageSizeOptions } = usePagination();
  const { logAction } = useAuditLog();

  const [internalFilter, setInternalFilter] = useState<'all' | 'external' | 'internal'>('external');

  // Get business filter from URL
  const businessFilter = searchParams.get('business') || '';

  // Fetch filter options
  const { data: businesses } = useAllBusinesses();
  const { data: exclusionRules, isLoading: exclusionRulesLoading } = useExclusionRules();

  const isFilteringByInternal = internalFilter !== 'all';

  const { data, isLoading, error } = useFAQAudits({
    page: isFilteringByInternal ? 1 : page,
    pageSize: isFilteringByInternal ? 10000 : pageSize,
    businessId: businessFilter || undefined,
  });

  const filteredData = data?.data.filter((item) => {
    if (internalFilter === 'all') return true;
    const excluded = isExcludedUser(item.businesses?.users?.email, exclusionRules);
    return internalFilter === 'internal' ? excluded : !excluded;
  });

  /**
   * Updates URL search params for filters.
   */
  const updateFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
    goToPage(1);
  };

  // Log page view
  useEffect(() => {
    logAction(AuditActionTypes.VIEW_FAQ_AUDIT_LIST);
  }, [logAction]);

  const columns: DataTableColumn<FAQAuditWithBusiness>[] = [
    {
      key: 'businesses',
      header: 'Business',
      render: (item) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (item.businesses?.id) {
              navigate(`/businesses/${item.businesses.id}`);
            }
          }}
          className="text-primary-600 hover:text-primary-700 font-medium"
        >
          {item.businesses?.business_name || '—'}
        </button>
      ),
    },
    {
      key: 'faq_score',
      header: 'FAQ Score',
      align: 'right',
      render: (item) => formatPercentage(item.faq_score),
    },
    {
      key: 'schema_score',
      header: 'Schema Score',
      align: 'right',
      render: (item) => formatPercentage(item.schema_score),
    },
    {
      key: 'recommended_faq',
      header: 'Recommended FAQs',
      align: 'center',
      render: (item) => item.recommended_faq?.length || 0,
    },
    {
      key: 'created_at',
      header: 'Date',
      sortable: true,
      render: (item) => (
        <span className="text-gray-500">{formatDate(item.created_at)}</span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <h1 className="text-2xl font-bold text-gray-900">FAQ Audits</h1>
        <p className="text-gray-500">
          View FAQ audit results and schema analysis scores
        </p>
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Business filter */}
          <select
            value={businessFilter}
            onChange={(e) => updateFilter('business', e.target.value)}
            className="input w-full sm:w-64"
          >
            <option value="">All businesses</option>
            {businesses?.map((b) => (
              <option key={b.id} value={b.id}>
                {b.business_name}
              </option>
            ))}
          </select>

          {/* Internal/External filter */}
          <select
            value={internalFilter}
            onChange={(e) => {
              setInternalFilter(e.target.value as 'all' | 'external' | 'internal');
              goToPage(1);
            }}
            disabled={exclusionRulesLoading}
            className="input w-full sm:w-48"
          >
            <option value="all">All results</option>
            <option value="external">External only</option>
            <option value="internal">Internal only</option>
          </select>

          {/* Clear filters button */}
          {(businessFilter || internalFilter !== 'external') && (
            <button
              onClick={() => {
                setSearchParams({});
                setInternalFilter('external');
              }}
              className="btn-secondary text-sm"
            >
              Clear filter
            </button>
          )}
        </div>
      </Card>

      {/* Error state */}
      {error && (
        <Card>
          <p className="text-error-600">Failed to load FAQ audits: {error.message}</p>
        </Card>
      )}

      {/* Data table */}
      <Card padding="none">
        <DataTable
          columns={columns}
          data={filteredData || []}
          isLoading={isLoading}
          emptyMessage="No FAQ audits found"
          keyExtractor={(item) => item.id}
        />
        {data && isFilteringByInternal ? (
          <div className="px-4 py-3 text-sm text-gray-500 border-t border-gray-200">
            {filteredData?.length ?? 0} {internalFilter} result{(filteredData?.length ?? 0) !== 1 ? 's' : ''} found
          </div>
        ) : data && data.totalPages > 0 ? (
          <Pagination
            currentPage={page}
            totalPages={data.totalPages}
            onPageChange={goToPage}
            pageSize={pageSize}
            onPageSizeChange={changePageSize}
            totalItems={data.count}
            pageSizeOptions={pageSizeOptions}
          />
        ) : null}
      </Card>

      {/* Detailed view */}
      {data?.data && data.data.length > 0 && (
        <Card title="Detailed View">
          <div className="space-y-3">
            {data.data.slice(0, 5).map((audit) => (
              <FAQDetailViewer key={audit.id} audit={audit} />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
